import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/i18n";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-ink border border-line px-4 py-3 text-sm text-bone placeholder:text-faint focus:outline-none focus:border-blaze transition-colors duration-200";

export default function AuthModal() {
  const { t, lang } = useLanguage();
  const { authOpen, setAuthOpen, login, register } = useAuth();
  const [mode, setMode] = useState("login"); // login | register | forgot
  const [form, setForm] = useState({ name: "", email: "", password: "", vehicle: "" });
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const close = () => {
    setAuthOpen(false);
    setError("");
    setSent(false);
    setMode("login");
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (mode === "login") {
        await login(form.email, form.password);
        toast.success(t.auth.welcome);
        close();
      } else if (mode === "register") {
        await register({
          name: form.name,
          email: form.email,
          password: form.password,
          vehicle: form.vehicle || undefined,
        });
        toast.success(t.auth.registered);
        close();
      } else {
        await axios.post(`${API}/auth/forgot-password?lang=${lang}`, { email: form.email });
        setSent(true);
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || t.auth.genericError);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {authOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-ink/80 backdrop-blur-sm"
          onClick={close}
          data-testid="auth-modal"
        >
          <motion.div
            initial={{ opacity: 0, y: 36, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md border border-line bg-surface p-7 sm:p-9"
          >
            <button
              data-testid="auth-modal-close-btn"
              onClick={close}
              className="absolute top-4 right-4 text-sandmut hover:text-blaze transition-colors"
            >
              <X size={18} />
            </button>

            <p className="font-telemetry text-[10px] uppercase tracking-[0.25em] text-blaze mb-2">
              Dust Republic — Members
            </p>
            <h3 className="font-display text-3xl font-extrabold uppercase tracking-tight text-bone mb-7">
              {mode === "login" ? t.auth.loginTitle : mode === "register" ? t.auth.registerTitle : t.auth.forgotTitle}
            </h3>
            {mode === "forgot" && (
              <p className="text-xs text-ash -mt-4 mb-5 leading-relaxed">{t.auth.forgotSub}</p>
            )}

            <form onSubmit={submit} className="flex flex-col gap-4" data-testid="auth-form">
              {mode === "register" && (
                <>
                  <input
                    data-testid="auth-register-input-name"
                    required
                    value={form.name}
                    onChange={set("name")}
                    placeholder={t.auth.name}
                    className={inputCls}
                  />
                  <input
                    data-testid="auth-register-input-vehicle"
                    value={form.vehicle}
                    onChange={set("vehicle")}
                    placeholder={t.auth.vehicle}
                    className={inputCls}
                  />
                </>
              )}
              <input
                data-testid="auth-login-input-email"
                type="email"
                required
                value={form.email}
                onChange={set("email")}
                placeholder={t.auth.email}
                className={inputCls}
              />
              {mode !== "forgot" && (
                <input
                  data-testid="auth-login-input-password"
                  type="password"
                  required
                  minLength={6}
                  value={form.password}
                  onChange={set("password")}
                  placeholder={t.auth.password}
                  className={inputCls}
                />
              )}

              {error && (
                <p data-testid="auth-error-alert" className="text-xs text-[#E86A50] border border-[#D94235]/40 bg-[#D94235]/10 px-3 py-2.5">
                  {error}
                </p>
              )}

              {sent && (
                <p data-testid="auth-forgot-success-alert" className="text-xs text-[#7FB383] border border-[#4E8752]/40 bg-[#4E8752]/10 px-3 py-2.5">
                  {t.auth.forgotSuccess}
                </p>
              )}

              <button
                data-testid="auth-login-submit-button"
                type="submit"
                disabled={busy}
                className="mt-1 font-telemetry text-xs uppercase tracking-[0.22em] bg-blaze text-ink px-6 py-3.5 hover:bg-sand transition-colors duration-300 disabled:opacity-60"
              >
                {mode === "login" ? t.auth.loginCta : mode === "register" ? t.auth.registerCta : t.auth.forgotCta}
              </button>
            </form>

            <div className="mt-5 flex items-center justify-between gap-3">
              <button
                data-testid="auth-switch-to-register-btn"
                onClick={() => {
                  setMode(mode === "register" ? "login" : "register");
                  setError("");
                  setSent(false);
                }}
                className="font-telemetry text-[10px] uppercase tracking-[0.2em] text-sandmut hover:text-blaze transition-colors"
              >
                {mode === "register" ? t.auth.switchToLogin : t.auth.switchToRegister}
              </button>
              <button
                data-testid="auth-forgot-password-link"
                onClick={() => {
                  setMode(mode === "forgot" ? "login" : "forgot");
                  setError("");
                  setSent(false);
                }}
                className="font-telemetry text-[10px] uppercase tracking-[0.2em] text-sandmut hover:text-blaze transition-colors"
              >
                {t.auth.forgotLink}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
